import { useEffect, useState } from 'react';

import { translate } from '../../../common/helpers/translate.helper';
import { Spinner } from '../../../common/Spinner/Spinner';
import { Box } from '../../../common/ui/Box';
import { Button } from '../../Button/Button';
import { ComboBoxOption, MultiComboBox } from '../../ComboBox/ComboBox';
import { Text } from '../../Text/Text';
import { FilterPill, FilterPillProps } from '../FilterPill/FilterPill';

import { getFilterValueLabel } from './util/getFilterValueLabel';

interface FilterMultiSelectFullProps {
    label: FilterPillProps['label'];
    filterByLabel: string;
    filterPlaceholder?: string;
    options: ComboBoxOption[];
    values?: string[];
    onChange?: (values: string[]) => void;
    sortOptions?: (a: ComboBoxOption, b: ComboBoxOption) => number;
    isLoading?: boolean;
    isError?: boolean;
}

export function FilterMultiSelectFull({
    label,
    filterByLabel,
    filterPlaceholder,
    values,
    options,
    onChange,
    sortOptions,
    isLoading,
    isError,
}: FilterMultiSelectFullProps) {
    const [visibleOptions, setVisibleOptions] = useState<string[]>(values || []);

    useEffect(() => {
        setVisibleOptions(values || []);
    }, [values]);

    const sortedOptions = [...options].sort(sortOptions || ((a, b) => a.label.localeCompare(b.label)));

    return (
        <FilterPill
            label={label}
            value={getFilterValueLabel(
                visibleOptions
                    .map((value) => options.find((it) => it.value === value))
                    .filter((it): it is ComboBoxOption => it !== undefined)
            )}
            onClear={() => onChange?.([])}
            onPopoverClose={() => {
                setVisibleOptions(values || []);
            }}
        >
            {(closePopover) => {
                if (isLoading) {
                    return (
                        <Box padding="12px" display="flex" justifyContent="center">
                            <Spinner />
                        </Box>
                    );
                }

                return (
                    <Box padding="12px" display="flex" flexDirection="column" gap={16}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={16}>
                            <Text variant="body" fontWeight={600}>
                                {filterByLabel}
                            </Text>
                            <Button
                                type="submit"
                                size="small"
                                emphasis="high"
                                onClick={() => {
                                    onChange?.(visibleOptions);
                                    closePopover();
                                }}
                            >
                                {translate('global.filterMultiSelect.apply')}
                            </Button>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={8}>
                            <MultiComboBox
                                negative={isError}
                                caption={isError ? translate('global.genericError') : undefined}
                                searchPlaceholder={filterPlaceholder}
                                onChange={setVisibleOptions}
                                value={visibleOptions}
                                options={sortedOptions}
                            />
                        </Box>
                    </Box>
                );
            }}
        </FilterPill>
    );
}
